// ============================================================
// calendarAnchorPref.ts
// Persists the Calendar/Diary's focus: the Day/Week ANCHOR day + the Month
// view's month, in localStorage — the same per-device persistence
// calendarViewPref already gives the view mode. Without this, creating an
// item from the calendar (task → TasksView, or "Open case →") UNMOUNTS
// CalendarView; on return it REMOUNTS and its local anchorDate/monthDate
// would re-init to TODAY, snapping the user off the day/month they were on.
// Reading + writing these keeps the anchor across the create round-trip
// (and across a reload — consistent with the persisted view mode).
//
// Plain read/write helpers (no zustand): nothing else needs to REACT to
// the anchor changing — only CalendarView reads it on mount and writes it
// when its own state changes — so a store subscription would just add
// redundant re-renders.
// ============================================================
const ANCHOR_KEY = 'casevo_calendar_anchor' // 'YYYY-MM-DD' (Day/Week focus day)
const ANCHOR_AT_KEY = 'casevo_calendar_anchor_at' // 'YYYY-MM-DD' (civil day the anchor was written)
const MONTH_KEY = 'casevo_calendar_month'    // 'YYYY-MM'    (Month view month)
const MONTH_AT_KEY = 'casevo_calendar_month_at' // 'YYYY-MM-DD' (civil day the month was written)

const ymd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

// Local-midnight of today (the fallback when nothing is stored / storage
// is blocked / the value is malformed).
const todayLocal = (): Date => {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate())
}

export const readAnchorDate = (): Date => {
  const today = todayLocal()
  try {
    const s = localStorage.getItem(ANCHOR_KEY)
    const m = s ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(s) : null
    if (m) {
      const stored = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      // ★ Honor a SAME-DAY anchor EXACTLY — even one pointing at a past day.
      // Opening a task/case from the calendar UNMOUNTS CalendarView, so on
      // return we re-read this; a user who had navigated back to an earlier
      // day must land back on THAT day, not today. The write-date companion
      // tells us the anchor was set during today's civil day (this session),
      // so it's a live focus, not a stale leftover.
      const writtenAt = localStorage.getItem(ANCHOR_AT_KEY)
      if (writtenAt === ymd(today)) return stored
      // ★ Otherwise the anchor is STALE — written on an EARLIER civil day (app
      // left open / reopened later). If it's now BEFORE today, snap to today so
      // the Day/Week view opens on the correct current day (right weekday), not
      // yesterday (#442). Local-parts comparison — no UTC shift.
      return stored.getTime() < today.getTime() ? today : stored
    }
  } catch {
    /* storage blocked — fall through to today */
  }
  return today
}

export const writeAnchorDate = (d: Date): void => {
  try {
    localStorage.setItem(ANCHOR_KEY, ymd(d))
    // Stamp the civil day this anchor was written so readAnchorDate can tell a
    // live same-session focus (honor exactly, even a past day) from a stale
    // leftover from a previous day (snap to today).
    localStorage.setItem(ANCHOR_AT_KEY, ymd(todayLocal()))
  } catch {
    /* storage blocked — keep the in-memory value */
  }
}

// First-of-month for the stored Month view month (default: this month).
// ★ Same staleness rule as readAnchorDate (#442), which the month never had:
// without it a month written in SEPTEMBER kept re-opening the Month view on
// September in OCTOBER (the "today" ring was right, the header was not).
//   • written TODAY (this session's round-trip) → honour it exactly, even a
//     past month the user navigated back to;
//   • written on an EARLIER day, or a legacy value with no stamp → it's a
//     stale leftover: if it's before the current month, open on THIS month.
// Local parts throughout (no UTC shift at a month boundary).
export const readMonthDate = (): Date => {
  const n = new Date()
  const thisMonth = new Date(n.getFullYear(), n.getMonth(), 1)
  try {
    const s = localStorage.getItem(MONTH_KEY)
    const m = s ? /^(\d{4})-(\d{2})$/.exec(s) : null
    if (m) {
      const stored = new Date(Number(m[1]), Number(m[2]) - 1, 1)
      if (localStorage.getItem(MONTH_AT_KEY) === ymd(todayLocal())) return stored
      return stored.getTime() < thisMonth.getTime() ? thisMonth : stored
    }
  } catch {
    /* storage blocked — fall through to this month */
  }
  return thisMonth
}

export const writeMonthDate = (d: Date): void => {
  try {
    localStorage.setItem(MONTH_KEY, `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
    // Stamp the civil day it was written (see readMonthDate).
    localStorage.setItem(MONTH_AT_KEY, ymd(todayLocal()))
  } catch {
    /* storage blocked */
  }
}
